import type { AxiosRequestConfig } from "axios";
/**
 * Mirrors: api/internal/auth/handlers.go (Handlers.Routes, mounted at
 * /api/v1/auth by httpapi.DefaultAuthPrefix).
 *
 *   GET   /auth/login      redirect to the identity provider  (navigate, do not fetch)
 *   GET   /auth/callback   the provider's return leg
 *   POST  /auth/logout     ends this session
 *   POST  /auth/logout-all ends every session of this operator
 *   GET   /auth/me         the PORTAL USER ROW — the one role authority
 *   PATCH /auth/me         display name only
 *
 * The role in `Me` comes from portal_users, never from a token claim: the
 * handler reads it out of the request context that the guarding tier populated
 * from the database on this request.
 */
import { get, patch, post, LOGIN_PATH, LOGOUT_PATH } from "@/lib/api";
import type { Role } from "./types";

/** auth.meSessionBlock. Null on the parent when the request carries no session (dev mode). */
export interface MeSession {
  id: string;
  user_name: string;
  expires_at: string;
  last_seen_at: string;
  created_at: string;
}

/** auth.meResponse. */
export interface Me {
  id: string;
  sub: string;
  email: string;
  name: string;
  role: Role;
  revoked: boolean;
  created_at: string;
  /** null means this operator has NEVER signed in. Not a creation date. */
  last_login_at: string | null;
  session: MeSession | null;
  dev_mode: boolean;
}

/** POST /auth/logout. `end_session_url` is null when the provider published none. */
export interface LogoutResult {
  end_session_url: string | null;
}

export interface LogoutAllResult {
  sessions_deleted: number;
  identity_provider_note: string;
}

export const authKeys = {
  me: () => ["auth", "me"] as const,
};

/**
 * The auth endpoints live at the ROOT, not under the /api/v1 baseURL the shared
 * axios instance carries — AUTH_REDIRECT_URL is registered with the identity
 * provider and must not move when the API version does.
 *
 * `baseURL: ""` is what escapes the prefix. Without it every call here resolves
 * to /api/v1/auth/* and 404s, which is precisely the defect that leaves
 * sp-migrate unable to log in or out.
 */
const atRoot: AxiosRequestConfig = { baseURL: "" };

export function fetchMe(): Promise<Me> {
  return get<Me>("/auth/me", atRoot);
}

export function updateMe(name: string): Promise<Me> {
  return patch<Me>("/auth/me", { name }, atRoot);
}

export function logout(): Promise<LogoutResult> {
  return post<LogoutResult>("/auth/logout", undefined, atRoot);
}

export function logoutAll(): Promise<LogoutAllResult> {
  return post<LogoutAllResult>("/auth/logout-all", undefined, atRoot);
}

/**
 * The URL to NAVIGATE to in order to sign in. GET /api/v1/auth/login answers
 * 302 to the identity provider, so it must be a document navigation — an XHR
 * would follow the redirect inside the fetch and land nowhere useful.
 */
export function loginHref(returnTo: string): string {
  return `${LOGIN_PATH}?return_to=${encodeURIComponent(returnTo)}`;
}

/** Exported so a caller never has to spell the logout route a second time. */
export const logoutPath = LOGOUT_PATH;
